import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Card from "components/card/Card";
import {
  RANK_RAMP,
  isRegionResponse,
  type RegionResponse,
} from "pages/cpiReports/regionMapOptions";
import europeOutline from "pages/cpiReports/europeOutline.json";
import { formatDecimal } from "utils/localeFormat";
import { EMISSIONS_MAP_LINK } from "pages/cpiReports/anchors";
import {
  fitProjection,
  projectOutline,
  projectRegions,
  topRegions,
  formatSharePct,
  formatTonnes,
} from "../regionMiniMap";
import { isTileLoading } from "../overviewData";

/**
 * Where a tenant's emissions start, as a dashboard card.
 *
 * A thumbnail of the real regions rather than the real map: see regionMiniMap.ts
 * for why the landing page does not load deck.gl. The "Open full map" link is the
 * only affordance that promises interaction, so nothing here should look
 * draggable.
 */

/** Matches the aspect the viewBox is projected into. */
const VIEW_W = 560;
const VIEW_H = 300;

interface QueryLike<T> {
  data: T | null | undefined;
  isError: boolean;
  refetch: () => void;
}

interface EmissionsMapSectionProps {
  regions: QueryLike<RegionResponse>;
}

const EmissionsMapSection: FC<EmissionsMapSectionProps> = ({ regions }) => {
  const { t, i18n } = useTranslation();
  // optional: the formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  /**
   * Validated once, here, rather than re-guarded at every read below. `regions.data`
   * is typed as `RegionResponse` but that is only an axios cast — nothing enforces
   * it on the wire — and several reads further down (`.totals.emissionWtwKg`,
   * `.coverage.excludedNoRegion`) only optional-chain the OUTER `data`, not the
   * nested field, so a malformed payload with a `data` key present but no
   * `totals`/`coverage` would still crash on those. Coercing to null here once
   * makes every `data?.` below actually safe.
   */
  const data = regions.data && isRegionResponse(regions.data) ? regions.data : null;
  const totalKg = data?.totals.emissionWtwKg ?? 0;

  /**
   * Fitted to the tenant's own footprint when there is one, and to Europe when
   * there is not — so the frame always holds a recognisable map instead of an
   * empty box. An empty box reads as a broken chart; an outline with "no pickup
   * locations yet" reads as the true state.
   */
  const projection = useMemo(() => {
    const pts = (data?.cells ?? []).flatMap((c) => c.boundary ?? []);
    return fitProjection(pts as [number, number][], VIEW_W, VIEW_H);
  }, [data]);

  const outline = useMemo(
    () => projectOutline(europeOutline.features as never, projection),
    [projection]
  );

  const shapes = useMemo(
    () => (data ? projectRegions(data.cells, totalKg, projection) : []),
    [data, totalKg, projection]
  );
  const rows = useMemo(
    () => (data ? topRegions(data.cells, totalKg) : []),
    [data, totalKg]
  );

  const loading = isTileLoading(regions);
  const excluded = data?.coverage.excludedNoRegion ?? 0;

  return (
    <div className="overview-map">
      <Card customClass="overview-map__card">
        <div className="overview-chart-card__head">
          <span className="overview-chart-card__title">
            {t("dashboard.emissionsByRegion", "Emissions by pickup region")}
          </span>
          <span className="overview-chart-card__sub">
            {t("dashboard.regionCount", "{{regions}} regions", {
              regions: shapes.length,
            })}
          </span>
        </div>

        <div className="overview-map__plot">
          {loading && <div className="overview-map__skeleton" aria-busy="true" />}

          {!loading && (
            <svg
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              className="overview-map__svg"
              role="img"
              aria-label={t(
                "dashboard.emissionsByRegion",
                "Emissions by pickup region"
              )}
            >
              {/* Context, never data: country outlines sit behind the regions and
                  are all that remains when a tenant has nothing to place. */}
              {outline.map((points) => (
                <polygon
                  key={points.slice(0, 32)}
                  points={points}
                  className="overview-map__outline"
                />
              ))}
              {shapes.map((s) => (
                <polygon key={s.id} points={s.points} fill={s.fill}>
                  {/* Native <title>, not a custom tooltip: this card is a summary,
                      and a hover layer here would imply an interactivity the
                      thumbnail deliberately does not have. */}
                  <title>
                    {`${s.name ?? s.id} — ${formatSharePct(s.sharePct, lng)} · ${formatTonnes(
                      s.tonnes,
                      lng
                    )}`}
                  </title>
                </polygon>
              ))}
            </svg>
          )}

          {!loading && shapes.length === 0 && (
            <p className="overview-map__empty" role="status">
              {t(
                "dashboard.noRegions",
                "No pickup locations could be placed on a map yet."
              )}
            </p>
          )}
        </div>

        <div className="overview-map__foot">
          {/* Labelled as a RANKING and carrying no numbers, exactly as on the full
              map: real tenants are skewed enough that a magnitude ramp leaves most
              regions indistinguishable. */}
          <span className="overview-map__legend">
            {t("dashboard.legendLower", "Lower")}
            <span className="overview-map__ramp" aria-hidden="true">
              {RANK_RAMP.map((c) => (
                <i key={c} style={{ background: c }} />
              ))}
            </span>
            {t("dashboard.legendHigher", "Higher")}
            <span className="overview-map__legend-note">
              &middot; {t("dashboard.byRankNotAmount", "by rank, not amount")}
            </span>
          </span>
          {/* Deep-links to the map itself, not just to the page that contains it.
              The map sits below several chart grids, so landing at the top of
              /cpi-reports is not "opening the full map" — the fragment is what
              the page scrolls to once the map has rendered. (Earlier this
              pointed at /reports, the embedded-report page, which answers "no
              report has been set up for your account yet".) */}
          <Link className="overview-map__link" to={EMISSIONS_MAP_LINK}>
            {t("dashboard.openFullMap", "Open full map")} &rarr;
          </Link>
        </div>
      </Card>

      <Card customClass="overview-map__card">
        <div className="overview-chart-card__head">
          <span className="overview-chart-card__title">
            {t("dashboard.biggestRegions", "Biggest regions")}
          </span>
          <span className="overview-chart-card__sub">
            {t("dashboard.shareOfTotal", "share of total")}
          </span>
        </div>

        {loading ? (
          <div className="overview-map__skeleton overview-map__skeleton--list" aria-busy="true" />
        ) : (
          <ul className="overview-top">
            {rows.map((r) => (
              <li key={r.id} className="overview-top__row">
                <span className="overview-top__swatch" style={{ background: r.fill }} />
                <span className="overview-top__name" title={r.name ?? r.id}>
                  {r.name ?? r.id}
                </span>
                <span className="overview-top__bar" aria-hidden="true">
                  <i style={{ width: `${Math.min(r.sharePct, 100)}%` }} />
                </span>
                <span className="overview-top__val">
                  {formatSharePct(r.sharePct, lng)}
                  <em>{formatTonnes(r.tonnes, lng)}</em>
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Pinned to the bottom of the card — see the SCSS. The map hides regions
            it cannot place, and saying so here is the only disclosure this card
            has room for. */}
        {excluded > 0 && (
          <p className="overview-map__note">
            {t(
              "dashboard.regionsExcluded",
              "{{shipments}} shipments start outside any known region and are not shown.",
              { shipments: formatDecimal(excluded, 0, lng) }
            )}
          </p>
        )}
      </Card>
    </div>
  );
};

export default EmissionsMapSection;
